import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Home as HomeIcon, Shield, Users } from 'lucide-react';
import logo from '../assets/logo.png';

const Home: React.FC = () => {
  const featuredProjects = [
    {
      id: 1,
      title: "Ursula",
      location: "Aurora, CO",
      status: "Completed",
      image: "/assets/pics/ursula/after/a0.jpg",
      price: "$420K"
    },
    {
      id: 2,
      title: "Chautauga Mountain",
      location: "Littleton, CO",
      status: "Completed",
      image: "/assets/pics/chautauga/after/01-DSC07347.jpg",
      price: "$875K"
    },
    {
      id: 3,
      title: "Valley Forge Dr",
      location: "Houston, TX",
      status: "Completed",
      image: "/assets/pics/valley/after/a1.jpeg",
      price: "$849K"
    },
    {
      id: 4,
      title: "South Circle",
      location: "Fonderen",
      status: "Completed",
      image: "/assets/pics/south_circle/before/b1.jpeg",
      price: "$749K"
    },
    {
      id: 5,
      title: "Meadowlake",
      location: "Shadybriar",
      status: "Completed",
      image: "/assets/pics/meadowlake/after/a3.0.jpeg",
      price: "$499K"
    }
    
  ];

  const [currentSlide, setCurrentSlide] = useState(0);
  const itemsPerView = 3; // Number of items visible at once (lg screens)
  const maxSlide = Math.max(0, featuredProjects.length - itemsPerView);
  

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => Math.min(prev + 1, maxSlide));
  }, [maxSlide]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => Math.max(prev - 1, 0));
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        nextSlide();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [nextSlide, prevSlide]);

  const features = [
    {
      icon: <HomeIcon className="w-8 h-8 text-golden" />,
      title: "Historic Preservation",
      description: "We maintain the architectural integrity while modernizing for contemporary living."
    },
    {
      icon: <Shield className="w-8 h-8 text-golden" />,
      title: "Quality Assurance",
      description: "Every renovation meets the highest standards of craftsmanship and materials."
    },
    {
      icon: <Users className="w-8 h-8 text-golden" />,
      title: "Expert Team",
      description: "Our specialists bring decades of experience in historic property restoration."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1920&h=1080&fit=crop"
            alt="Historic House"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center text-white px-4">
        <div className="mb-8">
          <div className="w-20 h-20 rounded-full overflow-hidden flex items-center justify-center mx-auto mb-6 scale-in hover-scale">
            <img
              src={logo}
              alt="Golden Generation Logo"
              className="w-full h-full object-cover scale-150"
              style={{ transform: 'scale(1.5) translateX(-2%)' }}
            />
          </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-4 font-serif fade-in-up duration-10000">
              GOLDEN GENERATION
            </h1>
            <p className="text-xl md:text-2xl text-golden mb-6 fade-in-up stagger-1">
              INVESTMENT PARTNERS
            </p>
          </div>
          
          <h2 className="text-3xl md:text-5xl font-light mb-8 max-w-4xl mx-auto fade-in-up stagger-2">
            Restoring the Past, Investing in the Future
          </h2>
          
          <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-200 fade-in-up stagger-3">
            We transform historic properties into modern, valuable investments while preserving their timeless character.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center fade-in-up stagger-4">
            <Link to="/developments" className="btn-primary hover-lift">
              View Our Projects
              <ArrowRight className="ml-2 inline" size={20} />
            </Link>
            <Link to="/about" className="btn-secondary bg-transparent text-white border-white hover:bg-white hover:text-dark-gray hover-lift">
              Learn More
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-light-beige">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-dark-gray mb-4 fade-in-up">Why Choose Us</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto fade-in-up stagger-1">
              Our commitment to excellence and attention to detail sets us apart in the world of historic property investment.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
              key={index} 
              className={`text-center p-6 bg-white rounded-lg shadow-lg card-hover fade-in-up stagger-${index + 1} transform transition-transform duration-300 hover:scale-105`}
            >
            
                <h3 className="text-xl font-semibold text-dark-gray mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-dark-gray mb-4 fade-in-up">Featured Projects</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto fade-in-up stagger-1">
              Discover our latest transformations and see how we breathe new life into historic properties.
            </p>
          </div>
          
          <div className="relative">
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-in-out gap-8"
                style={{ 
                  transform: `translateX(calc(-${currentSlide} * (100% / ${itemsPerView})))`
                }}
              >
                {featuredProjects.map((project, index) => (
                  <div 
                    key={project.id} 
                    className="w-full md:w-1/2 lg:w-[calc((100%-4rem)/3)] flex-shrink-0"
                  >
                    <div className={`bg-white rounded-lg shadow-lg overflow-hidden group card-hover fade-in-up stagger-${index + 1}`}>
                      <div className="relative overflow-hidden">
                        <img
                          src={project.image}
                          alt={project.title}
                          className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute top-4 right-4">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            project.status === 'Completed' 
                              ? 'bg-green-500 text-white' 
                              : 'bg-golden text-white'
                          }`}>
                            {project.status}
                          </span>
                        </div>
                      </div>
                      
                      <div className="p-6">
                        <h3 className="text-xl font-semibold text-dark-gray mb-2">{project.title}</h3>
                        <p className="text-gray-600 mb-3">{project.location}</p>
                        <div className="flex justify-between items-center">
                          <span className="text-2xl font-bold text-golden">{project.price}</span>
                          <Link 
                            to="/developments"
                            className="text-golden hover:text-golden/80 font-medium flex items-center hover-bounce"
                          >
                            View Details
                            <ArrowRight className="ml-1" size={16} />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Slider Controls (outside cards) */}
            {currentSlide > 0 && (
              <button
                aria-label="Previous"
                onClick={prevSlide}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 bg-white shadow-lg rounded-full p-3 text-dark-gray hover:text-golden transition-colors z-10"
              >
                <ArrowLeft size={18} />
              </button>
            )}
            {currentSlide < maxSlide && (
              <button
                aria-label="Next"
                onClick={nextSlide}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 bg-white shadow-lg rounded-full p-3 text-dark-gray hover:text-golden transition-colors z-10"
              >
                <ArrowRight size={18} />
              </button>
            )}
          </div>
          
          <div className="text-center mt-12 fade-in-up">
            <Link to="/developments" className="btn-primary hover-lift">
              View All Projects
              <ArrowRight className="ml-2 inline" size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Inline Gallery Section (between Featured Projects and About) */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden group">
              <img
                src="/assets/pics/brochure/b1.jpg"
                alt="Featured"
                className="w-full h-auto object-contain transform transition-transform duration-300 group-hover:scale-105"
              />
            </div>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden group">
              <img
                src="/assets/pics/brochure/b2.jpg"
                alt="Featured"
                className="w-full h-auto object-contain transform transition-transform duration-300 group-hover:scale-105"
              />
            </div>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden group">
              <img
                src="/assets/pics/brochure/b3.jpg"
                alt="Featured"
                className="w-full h-auto object-contain transform transition-transform duration-300 group-hover:scale-105"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Snippet Section */}
      <section className="section-padding bg-dark-gray text-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in-left text-center lg:text-left">
              <h2 className="text-4xl font-bold mb-6">About Golden Generation</h2>
              <p className="text-xl text-gray-300 mb-6 max-w-2xl mx-auto lg:mx-0">
                We are a team of passionate investors and restoration experts who believe that 
                historic properties represent more than just real estate—they are pieces of 
                living history that deserve to be preserved and enhanced for future generations.
              </p>
              <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto lg:mx-0">
                Our approach combines deep respect for architectural heritage with modern 
                investment strategies, creating properties that offer both cultural value 
                and strong financial returns.
              </p>
              <Link to="/about" className="btn-secondary border-white text-white hover:bg-white hover:text-dark-gray hover-lift inline-block">
                Learn More About Us
              </Link>
            </div>
            
            <div className="relative fade-in-right">
              <img
                src="https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=800&h=600&fit=crop"
                alt="Restoration Process"
                className="rounded-lg shadow-2xl hover-glow"
              />
              <div className="absolute bottom-4 left-4 bg-golden text-white px-5 py-4 rounded-lg shadow-lg">
                <div className="text-2xl md:text-3xl font-bold">Years</div>
                <div className="text-xs md:text-sm">of Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-golden text-white">
        <div className="container-custom text-center">
          <h2 className="text-4xl font-bold mb-6 fade-in-up">Ready to Invest in History?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto fade-in-up stagger-1">
            Join us in preserving architectural heritage while building your investment portfolio. 
            Let's discuss how we can help you achieve your real estate investment goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center fade-in-up stagger-2">
            <Link to="/contact" className="bg-white text-golden hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors hover-lift">
              Get in Touch
            </Link>
            <Link to="/developments" className="border-2 border-white text-white hover:bg-white hover:text-golden px-8 py-4 rounded-lg font-semibold transition-colors hover-lift">
              View Projects
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
